import { apiGet, apiPatch, apiPost } from '../../utilities/apiClient';
import {
  ADD_ACCOUNTS,
  SET_ACCOUNT,
  SET_CURRENT_ACCOUNT,
  FETCHING_ACCOUNTS,
  FETCHED_ACCOUNTS,
  UPDATE_ACCOUNT_FEATURES,
} from '../constants';
import { getStoreState } from './_sessionIds';
import { fetchProjects } from './projects';

export const updateAccountFeatures = (updates: Record<string, any>) => async (dispatch: any) => {
  const state = getStoreState() as any;
  const accountId = state.session.account?.account_id;
  const cognitoId = state.authentication.user?.cognito_id;
  if (!accountId || !cognitoId) return { success: false };

  // Optimistically update Redux so the UI responds instantly
  dispatch({ type: UPDATE_ACCOUNT_FEATURES, payload: updates });

  try {
    const response = await apiPatch(
      `/accounts/update-account-features/${accountId}/${cognitoId}`,
      { updates },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// Partner-only: switch the active account to a customer the partner manages.
// The partner remains logged in as themselves (their cognito_id is unchanged
// in state.authentication.user), but session.account / accounts.current now
// point at the customer's account so all scoped API calls run against the
// customer's account_id. Mirrors the legacy frontend Access button on
// ProjectCard (frontend/client/src/Components/ProjectCard/index.js#L364).
//
// AppShell re-evaluates partner-vs-customer routing off session.account.partner,
// so dispatching this with a non-partner account_id swaps the user into the
// MainTabNavigator automatically. Use switchBackToPrimaryAccount() to undo.
export const switchToCustomerAccount =
  (targetAccount: { account_id: string; [k: string]: any }) =>
  async (dispatch: any) => {
    if (!targetAccount?.account_id) return { success: false };
    const state = getStoreState() as any;
    const cognitoId = state.authentication?.user?.cognito_id;
    dispatch({ type: SET_CURRENT_ACCOUNT, payload: targetAccount });
    dispatch({ type: SET_ACCOUNT, payload: targetAccount });

    // Merge the target into accounts.list so AccountsScreen + any other
    // "switch account" surface can offer it as a destination. Without
    // this, the partner has no UI path back out of the customer view —
    // the partner's account_memberships fetch never included the
    // customer (the only reason they can reach it at all is the
    // partner-membership lookup on the server).
    //
    // Dedupe by account_id, tag the target with `via_partner_access`
    // so the UI can render a "Viewing as partner" badge, and preserve
    // the existing primary so switchBackToPrimaryAccount() still
    // resolves the partner's own account.
    const existingList = Array.isArray(state.accounts?.list) ? state.accounts.list : [];
    const filtered = existingList.filter((a: any) => a?.account_id !== targetAccount.account_id);
    const taggedTarget = { ...targetAccount, via_partner_access: true };
    const nextList = [...filtered, taggedTarget];
    const primary = state.accounts?.primary
      || existingList.find((a: any) => a?.is_primary)
      || existingList[0]
      || null;
    dispatch({
      type: ADD_ACCOUNTS,
      payload: { accounts: nextList, current: taggedTarget, primary },
    });

    // Pre-warm the customer's project list so the destination ProjectHome
    // can render without an initial fetch flicker.
    if (cognitoId) {
      try { await dispatch(fetchProjects(targetAccount.account_id, cognitoId)); } catch { /* noop */ }
    }
    return { success: true };
  };

// Partner-only: return from a customer view to the partner's own account.
// Used by any "Exit customer view" affordance — falls back to the primary
// account if explicit `primary` isn't set yet. Pre-warms the partner's
// projects so the destination shell mounts without a fetch flicker
// (mirrors switchToCustomerAccount's symmetry).
export const switchBackToPrimaryAccount = () => async (dispatch: any) => {
  const state = getStoreState() as any;
  const cognitoId = state.authentication?.user?.cognito_id;
  const primary = state.accounts?.primary
    || (Array.isArray(state.accounts?.list)
      ? state.accounts.list.find((a: any) => a?.is_primary) || state.accounts.list[0]
      : null);
  if (!primary?.account_id) return { success: false };
  dispatch({ type: SET_CURRENT_ACCOUNT, payload: primary });
  dispatch({ type: SET_ACCOUNT, payload: primary });
  if (cognitoId) {
    try { await dispatch(fetchProjects(primary.account_id, cognitoId)); } catch { /* noop */ }
  }
  return { success: true };
};

export const fetchAccounts = (accountId: string, cognitoId: string) => async (dispatch: any) => {
  dispatch({ type: FETCHING_ACCOUNTS });
  try {
    const response = await apiGet(`/accounts/get-accounts/${accountId}/${cognitoId}`);
    if (response?.success) {
      const accounts = (response.payload || []) as any[];
      // Mirror legacy frontend/client/src/store/actions/accounts.js: prefer
      // the account flagged is_current (the one we asked for in the URL),
      // fall back to is_primary (the user's own account, where account_id ===
      // their cognito_id), and only then to accounts[0]. Picking accounts[0]
      // blindly was a latent bug — Postgres' DISTINCT ON returns rows in an
      // unspecified order, so users with multiple memberships (e.g. their
      // personal account + an eleplan-admin account) sometimes landed on
      // the wrong default and saw "not authorized" errors against admin
      // endpoints because the URL's account_id didn't match the account
      // their eleplan-admin permission lived on.
      const fallback = Array.isArray(accounts) ? accounts[0] : null;
      const current =
        accounts.find((a: any) => a?.is_current) ||
        accounts.find((a: any) => a?.is_primary) ||
        fallback;
      const primary = accounts.find((a: any) => a?.is_primary) || fallback;
      dispatch({ type: ADD_ACCOUNTS, payload: { accounts, current, primary } });
      if (current) dispatch({ type: SET_CURRENT_ACCOUNT, payload: current });
      dispatch({ type: FETCHED_ACCOUNTS });
      // `account_closed` is set by get-accounts when there are no active
      // accounts but a status='closed' one exists — the login flow uses it to
      // show a "closed" screen + sign-out instead of re-provisioning.
      return { success: true, accounts, current, primary, account_closed: !!response.account_closed };
    }
    dispatch({ type: FETCHED_ACCOUNTS });
    return { success: false };
  } catch (err: any) {
    dispatch({ type: FETCHED_ACCOUNTS });
    return { success: false, message: err?.message };
  }
};

/**
 * Close (deactivate) the caller's own account — reversible status flip to
 * 'inactive' server-side. The caller should sign the user out on success.
 */
export const closeMyAccount = async (
  accountId: string,
  cognitoId: string,
): Promise<{ success: boolean; message?: string }> => {
  if (!accountId || !cognitoId) return { success: false, message: 'Missing account identifiers' };
  try {
    const res = await apiPost(`/accounts/close/${accountId}/${cognitoId}`, {});
    if (res?.success) return { success: true };
    return { success: false, message: res?.message || 'Could not close the account.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not close the account.' };
  }
};
